/**
 * eu 站渲染转发代理商规则
 */
import { Button, Tooltip } from '@bfe/moly';
import clx from 'classnames';
import React, { FC, useEffect, useMemo, useState } from 'react';

import Ready from '@/assets/icon/account/ready.svg';
import AccountBasicItem from '@/components/AccountBasicItem';
import { useUserProfile } from '@/hooks/useUserProfile';
import { useI18nContext } from '@/i18n/I18n';
import { getUserCodeInfo } from '@/services/affiliate';
import { useStore } from '@/store/useStore';
import { useUserStore } from '@/store/useUser';
import { complianceHide, complianceShow } from '@/utils/complianceHelpers';
import { REGULATED_SITE_HOSTS } from '@/utils/constants/compliance';

import AffiliateIcon from './affiliateIcon';

const AffiliateOption: FC = () => {
  const { complianceConfig } = useUserStore();
  const {
    loading,
    isSubmember,
    isRegisteOverTime,
    id: userId,
  } = useUserProfile();
  const { kyc } = useStore();
  const [$t, lang] = useI18nContext();
  const [loadingAffInfo, setLoadingAffInfo] = useState(true);
  const [userCodeInfo, setUserCodeInfo] = useState<any>({});

  // 显示条件：
  // 在注册十四天内 AND 对于没有绑定代理/好友的用户（no referral code）AND 用户是用主账户 AND 用户不是INS 或 broker，显示入口
  // 或者
  // 注册180天未交易的母账号，且用户不是INS或broker
  const isShowBindEntry = useMemo(() => {
    // 接口没有返回前都是隐藏状态
    if (loading || loadingAffInfo || !Object.keys(userCodeInfo).length) {
      return false;
    }

    if (!isRegisteOverTime) {
      return (
        !userCodeInfo?.isBeInvited && // 如果是被邀请好友，返回 true
        !userCodeInfo?.affId && // 代理商id，如果是匿名代理商返回 ""
        !userCodeInfo?.isPro && // 是否是pro用户
        !isSubmember // 是否子账号
      );
    }

    // 注册180天未交易
    if (userCodeInfo?.is180) {
      return (
        !userCodeInfo?.isBeInvited &&
        !userCodeInfo?.isPro &&
        !isSubmember &&
        REGULATED_SITE_HOSTS === 'BYBIT'
      ); // 主账号且用户不是INS和broker
    }

    return false;
  }, [loading, loadingAffInfo, userCodeInfo, isSubmember, isRegisteOverTime]);

  useEffect(() => {
    if (!loading && userId > 0) {
      getUserCodeInfo({ source: isRegisteOverTime ? 2 : 0 })
        .then((res: any) => {
          setLoadingAffInfo(false);
          setUserCodeInfo(res?.result);
        })
        .catch(() => {
          setLoadingAffInfo(false);
        });
    }
  }, [loading, userId, isRegisteOverTime]);

  const { affId, groupId, isAnonymous, nickName, euRewardShow } = userCodeInfo;
  const { hideAffiliate, AffiliateEuReward } = complianceConfig;

  // 代理商并且非匿名用户
  const isAffAndNoAnonymous = !isAnonymous && affId;

  const noticeText = (
    $t('userCodeInfo') === 'userCodeInfo'
      ? $t('affiliate info template')
      : $t('userCodeInfo')
  )
    ?.replace('{name}', userCodeInfo?.nickName)
    ?.replace('{aff_id}', userCodeInfo?.affId);

  return (
    <>
      {/**
       *  1. 有代理商并且非匿名
       *  或
       *  2. 注册不到14天
       *
       *  必须 ：母账户 & 之前的合规逻辑
       */}

      {(isAffAndNoAnonymous || isShowBindEntry) &&
        !complianceHide(hideAffiliate) && (
          <AccountBasicItem
            icon="AffiliateIcon"
            molyIcon={<AffiliateIcon />}
            text={
              affId
                ? $t(`Affiliate's community`)
                : $t(`Join an Affiliate's community`)
            }
            subText={
              complianceShow(AffiliateEuReward) &&
              euRewardShow === 1 && (
                <>
                  {/*
                    1. 第一个是 bybit 站点 & eea 标签 （在合规配置平台获取的配置）
                    2. 后台接口告知我们是否为白名单
                  */}
                  <span>
                    {$t('jump affiliate')
                      .replace('{aff_name}', nickName)
                      .replace('{aid}', affId)}
                    <a
                      style={{ marginLeft: '4px' }}
                      onClick={(evt: any) => {
                        if (evt.target?.nodeName.toLowerCase() === 'a') {
                          const _url = `https://www.bybit.eu/sign-up?affiliate_id=${affId}&group_id=${groupId}&group_type=1`;
                          window.location.href = _url;
                        }
                      }}
                    >
                      {$t('click here')}
                    </a>
                  </span>
                </>
              )
            }
            operation={
              <>
                {affId && (
                  <div className={clx('account-status account-status-ready ')}>
                    <div
                      className="truncate"
                      style={{
                        width: '140px',
                      }}
                    >
                      {/* 绑定了非匿名代理商，展示代理信息, 香港站不允许绑定 */}

                      <Tooltip title={noticeText}>
                        <div className={clx('items-center inline-flex gap-1')}>
                          <img
                            src={Ready}
                            alt="ready"
                            className="status-ready-img"
                          />
                          <span
                            className="truncate"
                            style={{
                              width: '126px',
                            }}
                          >
                            {noticeText}
                          </span>
                        </div>
                      </Tooltip>
                    </div>
                    <div style={{ minWidth: '208px' }}></div>
                  </div>
                )}

                {isShowBindEntry &&
                  !affId &&
                  !complianceHide(hideAffiliate) && (
                    <Button
                      size="small"
                      data-radius="small"
                      variant="primary"
                      onClick={() => {
                        window.location.href = `/${lang}/bind-code?from=accountInfo&source=${
                          isRegisteOverTime ? 2 : 0
                        }`;
                      }}
                    >
                      {$t('Join')}
                    </Button>
                  )}
              </>
            }
          />
        )}
    </>
  );
};
export default AffiliateOption;
